import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React from "react";
import { useDispatch } from "react-redux";
import { openSnackBar } from "../../redux/actions/snackbar.actions";
import { taskDetailModalToggle } from "../../redux/actions/taskDetail.action";
import { getApiUrl } from "../../utils/apiUrls";
import instance from "../../utils/axios";
import { apiErrorHandler } from "../../utils/errorHandler";
import { taskCatImg } from "../../utils/taskCatImg";
import CustomToolTip from "../custom-tooltip/CustomTooltip.component";
import "./TaskItem.styles.css";
import LockCoverScreen from "../../assets/images/tasks/lock-candidate-banner.svg";
import { isValidUrl, isSecureUrl, sanitiseInput } from "../../utils/helperFn";

const NotCompletedTask = ({
  data,
  t,
  eventId,
  joinModalData,
  joinModalDataHandler,
  hasAllGroupBlocked,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExternal, setIsExternal] = React.useState(false);

  const hoganRef = React.useRef(null);

  React.useEffect(() => {
    if (
      data.taskMode === "OFFLINE" &&
      ["INTERVIEW", "GROUP_DISCUSSION"].includes(data.taskType) &&
      data?.location &&
      isValidUrl(data.location)
    )
      setIsExternal(true);
  }, []);

  const handleModal = () => {
    dispatch(taskDetailModalToggle({ ...data, eventId }));
  };

  const disabledButton = (
    val,
    tooltipMessage = null,
    icon,
    disableTooltip = false
  ) => {
    return (
      <CustomToolTip
        disableTooltip={disableTooltip} // default to show tooltip
        title={
          <p className="mb-0 text-white fw-medium cm-xs-txt">
            {!!tooltipMessage ? t(tooltipMessage) : t(val)}
          </p>
        }
      >
        <div className="d-flex" style={{ width: "calc(100% - 48px)" }}>
          <button className="btn justify-content-center cm-trans-btn cm-task-btn cm-locked-btn text-action-blue fw-medium cm-sm-txt cm-truncate-1 w-100">
            {t(val)}
            {!!icon ? <FontAwesomeIcon icon={icon} className="ps-2" /> : null}
          </button>
        </div>
      </CustomToolTip>
    );
  };

  const getThirdPartyTaskLink = async (type = null) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const res = await instance.post(
        getApiUrl("getThirdPartyLoginData"),
        {
          taskId: data.taskId,
          eventId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data) {
        if (type === "TTE_ASSESSMENTS") {
          if(!!res.data.url) {
            // Security: Validate URL to prevent Open Redirect (CWE-601)
            const secureUrl = isSecureUrl(res.data.url, dispatch, t);
            if (!secureUrl) {
              setIsLoading(false);
              return;
            }
            // Safe: URL has been validated above
            window.open(secureUrl, "_blank");
          }
          setIsLoading(false);
          return;
        } else {
          setIsLoading(false);
          
          // Security: Validate URL to prevent Open Redirect (CWE-601)
          const secureUrl = isSecureUrl(res.data.url, dispatch, t);
          if (!secureUrl) {
            return;
          }
          
          const params = res.data;
          const form = hoganRef.current;
          form.method = "POST";
          // Safe: URL has been validated above
          form.action = secureUrl;

          for (const key in params) {
            const input = document.createElement("input");
            if (input.name !== "url") {
              input.type = "hidden";
              input.name = key;
              input.value = params[key];
              form.appendChild(input);
            }
          }
          document.body.appendChild(form);
          form.submit();
          form.innerHTML = "";

          return;
        }
      }

      if (res.data.status === "ERROR") {
        throw Error(res.data.message);
      }
    } catch (error) {
      setIsLoading(false);
      const errObj = apiErrorHandler(error);

      dispatch(
        openSnackBar({
          msg: t(errObj.statusText),
          type: "error",
        })
      );
    }
  };

  const getTaskLink = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const res = await instance.post(
        getApiUrl("getTaskLink"),
        {
          taskId: data.taskId,
          eventId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.success) {
        setIsLoading(false);
        
        // Security: Validate URL to prevent Open Redirect (CWE-601)
        const secureUrl = isSecureUrl(res.data.testLink, dispatch, t);
        if (!secureUrl) {
          return;
        }
        
        // Safe: URL has been validated above
        window.open(secureUrl, "_blank");
      }

      if (res.data.status === "ERROR") {
        throw Error(res.data.message);
      }
    } catch (error) {
      setIsLoading(false);
      const errObj = apiErrorHandler(error);

      dispatch(
        openSnackBar({
          msg: t(errObj.statusText),
          type: "error",
        })
      );
    }
  };

  const eventHandler = (interviewLink, taskStartDate, showPopup) => {
    // Security: Validate URL to prevent Open Redirect (CWE-601)
    const secureUrl = isSecureUrl(interviewLink, dispatch, t);
    if (!secureUrl) {
      return;
    }
    
    if (showPopup) {
      joinModalDataHandler({
        ...joinModalData,
        customDialogBoxToggle: true,
        interviewLink: secureUrl,
        taskStartDate,
        taskType: data.taskType,
      });
    } else {
      // Safe: URL has been validated above
      window.open(secureUrl, "_blank");
    }
  };

  const loadButton = () => {
    if (data.taskControl === "LOCK" || data?.block) {
      return disabledButton("yet_to_be_unlocked", null, null, true);
    }
    if (data.taskType === "FEEDBACK_SURVEY") {
      // HANDLE ALL FEEDBACK SURVEY CASES
      if (moment().isAfter(moment(data.taskStartDate))) {
        let link;
        if (!!data.approverLink) {
          link = data.approverLink;
        } else if (!!data.nominationLink) {
          link = data.nominationLink;
        } else {
          link = data.selfLink;
        }
        return (
          <div
            className={`btn cm-trans-btn cm-task-btn fw-medium cm-sm-txt cm-truncate-1 text-action-blue`}
            onClick={() => {
              // Security: Validate URL to prevent Open Redirect (CWE-601)
              const secureUrl = isSecureUrl(link, dispatch, t);
              if (!secureUrl) return;
              // Safe: URL has been validated above
              window.open(secureUrl, "_blank");
            }}
          >
            {t("launch_task")}
            <FontAwesomeIcon icon={regular("external-link")} className="ps-2" />
          </div>
        );
      }
      return disabledButton("yet_to_be_unlocked", null, null, true);
    }
    if (data.taskMode === "OFFLINE") {
      // HANDLE ALL OFFLINE CASES
      if (["INTERVIEW", "GROUP_DISCUSSION"].includes(data.taskType)) {
        // INTERVIEW GROUP_DISCUSSION
        if (!!data?.location) {
          const tooltipMessage = `${
            data.taskType === "INTERVIEW"
              ? t("interview_location")
              : t("gd_location")
          }: ${data.location}`;
          if (isValidUrl(data.location)) {
            return !moment().isBetween(
              moment(data.taskStartDate),
              moment(data.taskEndDate)
            ) ? (
              disabledButton(
                t("launch_task"),
                null,
                regular("external-link"),
                true
              )
            ) : (
              // 1 location with link
              <a
                href={data.location}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn cm-trans-btn cm-task-btn fw-medium cm-sm-txt cm-truncate-1 text-action-blue`}
                style={{ width: "calc(100% - 48px)" }}
              >
                {t("launch_task")}
                <FontAwesomeIcon
                  icon={regular("external-link")}
                  className="ps-2"
                />
              </a>
            );
          }

          return (
            <CustomToolTip
              disableTooltip={data.block}
              title={
                <p className="mb-0 text-white fw-medium cm-xs-txt">
                  {tooltipMessage}
                </p>
              }
            >
              {/*2 location without link*/}
              <div style={{ width: "calc(100% - 48px)" }}>
                <button
                  className={`btn w-100 bg-white cm-task-btn cm-truncate-1 text-action-blue cm-pointer-none fw-medium cm-sm-txt ${
                    data.block
                      ? "cm-locked-btn border-blue-gray-300"
                      : "border-action-blue"
                  }`}
                >
                  {`${t("location")}: ${data.location}`}
                </button>
              </div>
            </CustomToolTip>
          );
        } else {
          return disabledButton("yet_to_be_scheduled");
        }
      } else {
        // TEST
        return disabledButton(
          "join_in_person",
          "this_task_is_to_be_attended_offline_in_person"
        );
      }
    }

    if (["INTERVIEW", "GROUP_DISCUSSION"].includes(data.taskType)) {
      // HANDLE ALL INTERVIEW GROUP_DISCUSSION CASES
      const showPopup = !moment().isSameOrAfter(
        moment(data.taskStartDate).subtract(15, "minutes")
      );
      
      const isAiTaskEnabled = moment().isSameOrAfter(moment(data.taskStartDate)) && moment().isSameOrBefore(moment(data.taskEndDate));

      return !!data.taskStartDate ? (data.taskMode === "AI" && data?.slotType && data.taskEndDate && !isAiTaskEnabled) ? (
        disabledButton("yet_to_be_unlocked")
      ) : (
        <div
          className={`btn cm-trans-btn cm-task-btn fw-medium cm-sm-txt cm-truncate-1 ${
            !showPopup
              ? "text-action-blue"
              : "text-blue-gray-700 border border-blue-gray-300"
          }`}
          style={{ width: "calc(100% - 48px)" }}
          onClick={() =>
            !data.block &&
            eventHandler(data.taskLink, data.taskStartDate, showPopup)
          }
        >
          {t("launch_task")}
          <FontAwesomeIcon icon={regular("external-link")} className="ps-2" />
        </div>
      ) : (
        disabledButton("yet_to_be_scheduled")
      );
    }

    if (["ASSESSMENT"].includes(data.taskType)) {
      if (!data.taskStartDate) {
        return disabledButton("yet_to_be_scheduled");
      }
    }

    if (moment().isBefore(data.taskStartDate)) {
      return disabledButton("yet_to_be_unlocked");
    }

    if (
      moment().isBetween(moment(data.taskStartDate), moment(data.taskEndDate))
    ) {
      return (
        <button
          title={t("launch_task")}
          className={`btn cm-trans-btn-light py-2 text-action-blue fw-medium cm-sm-txt cm-truncate-1 ${
            isLoading
              ? "cm-locked-btn border-blue-gray-300"
              : "border-action-blue"
          }`}
          onClick={() => {
            if (data.taskType === "HOGAN_ASSESSMENTS") {
              getThirdPartyTaskLink();
              return
            }
            if (data.taskType === "TTE_ASSESSMENTS") {
              getThirdPartyTaskLink("TTE_ASSESSMENTS");
              return
            }
            getTaskLink();
          }}
          style={{ width: "calc(100% - 48px)" }}
        >
          {t("launch_task")}
          <FontAwesomeIcon icon={regular("external-link")} className="ps-2" />
        </button>
      );
    }

    if (moment().isAfter(data.taskStartDate)) {
      return (
        <button
          title={t("launch_task")}
          className="btn cm-trans-btn cm-task-btn cm-locked-btn text-action-blue fw-medium cm-sm-txt"
        >
          {t("launch_task")}
          <FontAwesomeIcon icon={regular("external-link")} className="ps-2" />
        </button>
      );
    }

    return null;
  };

  const loadTaskTime = () => {
    if (data.taskControl === "LOCK" || data?.block) {
      return (
        <>
          {t("starts_on")}:
          <span className="text-blue-gray-700 fw-semibold">{` ${t(
            "yet_to_be_unlocked"
          )}`}</span>
        </>
      );
    }
    if (
      [
        "ASSESSMENT",
        "INTERVIEW",
        "GROUP_DISCUSSION",
        "HOGAN_ASSESSMENTS",
      ].includes(data.taskType)
    ) {
      let dateText = "",
        taskDate = "";
      if (!!data.taskStartDate) {
        dateText = moment().isAfter(moment(data.taskStartDate))
          ? `${t("deadline")}:`
          : `${t("starts_on")}:`;
        taskDate = moment().isAfter(moment(data.taskStartDate))
          ? moment(data.taskEndDate).format("MMM DD YYYY @ hh:mm A")
          : moment(data.taskStartDate).format("MMM DD YYYY @ hh:mm A");
      } else {
        dateText = `${t("starts_on")}:`;
        taskDate = t(data.taskStatus.toLowerCase());
      }
      return (
        <>
          {dateText}{" "}
          <span className="text-blue-gray-700 fw-semibold">{taskDate}</span>
        </>
      );
    }

    // FOR FEEDBACK SURVEY
    return (
      <>
        {moment().isAfter(moment(data.taskStartDate))
          ? t("deadline")
          : t("starts_on")}{" "}
        :{" "}
        <span className="text-blue-gray-700 fw-semibold">
          {moment(
            moment().isAfter(moment(data.taskStartDate))
              ? data.taskEndDate
              : data.taskStartDate
          ).format("MMM DD YYYY @ hh:mm A")}
        </span>
      </>
    );
  };

  const loadTaskCat = () => {
    if (data.taskControl === "LOCK" || data?.block) {
      return t("yet_to_be_unlocked");
    }
    if (data.taskType === "HOGAN_ASSESSMENTS") {
      return t("hogan_candidate_tasktype");
    }
    if (data.taskType === "TTE_ASSESSMENTS") {
      return t("mercer_talent_enterprise");
    }
    if (data.taskType === "FEEDBACK_SURVEY") {
      if (!!data?.modeType && data?.modeType !== "FEEDBACK_SURVEY") {
        return t(data.modeType.toLowerCase());
      }
      return t("360_survey");
    }
    if (data.taskMode === null)
      return data.taskType === "ASSESSMENT"
        ? t("test")
        : data.taskType === "BTA" ? t("business_thinking_appraisal") : t(data.taskType.toLowerCase());
    // return data.taskMode?.toLowerCase() === 'online' ? data.taskType === 'ASSESSMENT' ? t("test") : t(data.taskType.toLowerCase()) : data.modeType  === 'ASSESSMENT' ? t("test") : t(data.modeType.toLowerCase())
    if (
      ["online", "offline", "hybrid", "ai"].includes(data.taskMode?.toLowerCase())
    ) {
      if (!!data?.modeType) {
        return ["ASSESSMENT", "TESTS"].includes(data?.modeType)
          ? t("test")
          : data.taskType === "BTA" ? t("business_thinking_appraisal") : t(data.modeType.toLowerCase());
      } else {
        return ["ASSESSMENT", "TESTS"].includes(data?.taskType)
          ? t("test")
          : data.taskType === "BTA" ? t("business_thinking_appraisal") : t(data.taskType?.toLowerCase());
      }
    }
  };

  const showDurationHandler = () => {
    let label = "";
    let icon = regular("clock");
    if (data.taskControl === "LOCK" || data.block) {
      label = "--";
    } else if (
      data.taskType === "ASSESSMENT" ||
      "FEEDBACK_SURVEY" ||
      "HOGAN_ASSESSMENTS"
    ) {
      if (![null, 35791394, 0].includes(data.duration)) {
        label = `${data.duration} ${t("mins")}`;
      } else {
        icon = regular("hourglass-clock");
        label = t("candidate_side_deadline_based");
      }
    } else if (data?.duration) {
      label = `${data.duration} ${t("mins")}`;
    } else {
      label = "--";
    }
    return (
      <div className="d-flex ms-3">
        <FontAwesomeIcon
          icon={icon}
          className="text-blue-gray-500 cm-xs-txt me-6 my-auto"
        />
        <p className="cm-task-title text-blue-gray-700 my-0 fw-medium cm-xs-txt my-auto">
          {label}
        </p>
      </div>
    );
  };

  const handleBgImage = () => {
    if (data.taskControl === "LOCK" || data?.block) {
      return LockCoverScreen;
    }
    if(data.taskMode === "AI") {
      return taskCatImg("ai_led_session");
    }
    if (data.taskType === "TTE_ASSESSMENTS") {
      return taskCatImg(data.originalTaskName);
    }
    if (["HOGAN_ASSESSMENTS"].includes(data.taskType)) {
      switch (data.originalTaskName) {
        case "hogan_personality_inventory":
          return taskCatImg("hogan_hpi");
        case "hogan_development_survey":
          return taskCatImg("hogan_hds");
        case "motives_values_preferences_inventory":
          return taskCatImg("hogan_mvpi");
        default:
          return taskCatImg("hogan_hbri");
      }
    } else if (data.taskType === "FEEDBACK_SURVEY") {
      if (!!data?.modeType) {
        return taskCatImg(data.modeType);
      }
      return taskCatImg("FEEDBACK_SURVEY");
    }

    // FOR INTERVIEW GD AND TESTS
    return taskCatImg(
      data.taskMode === null
        ? data.taskType
        : !!data.modeType
        ? data.modeType
        : ""
    );
  };
  return (
    <div className="cm-task-item d-flex flex-column justify-content-between col-3">
      {data.taskType === "HOGAN_ASSESSMENTS" && (
        <form ref={hoganRef} target="_blank" />
      )}
      <div className="cm-task-item-content">
        <div className="cm-task-item-img position-relative">
          <img
            src={handleBgImage()}
            alt={data.taskName}
            className={`img-fluid`}
          />
          {data.taskControl !== "LOCK" &&
            data.taskMode === "OFFLINE" &&
            !!data.taskStartDate && (
              <span className="cm-offline-badge bg-blue-gray-700 border border-white position-absolute text-uppercase text-white cm-xs-txt fw-medium">
                {isExternal ? t("external") : t("offline_candidate")}
              </span>
            )}
          {(data.taskControl === "LOCK" || data?.block) && (
            <div className="cm-offline-badge position-absolute bg-white border border-danger text-danger cm-xs-txt">
              <FontAwesomeIcon icon={regular("lock")} className="pe-2" />
              <span className="fw-medium text-uppercase fw-medium">
                {t("locked")}
              </span>
            </div>
          )}
        </div>
        <p
          className="cm-task-title my-0 fw-semibold cm-truncate-1 px-3 pt-3 pb-2 cm-sm-txt"
          title={t(data.taskName)}
        >
          {t(data.taskName)}
        </p>
        <p className="cm-sm-txt cm-task-category my-0 text-blue-gray-500 px-3">
          {loadTaskCat()}
        </p>
        <hr className="my-12 mx-3" />
        <p className="ms-3 mb-10 d-inline-block my-0 cm-sm-txt text-blue-gray-500">
          {loadTaskTime()}
        </p>
      </div>
      {showDurationHandler()}
      <div className="cm-task-actions d-flex">
        {data.taskType !== "BTA" && loadButton()}
        <button
          className={`btn ${data.taskType === "BTA" ? "ms-0" : ""} cm-trans-btn cm-task-icon-btn cm-sm-txt`}
          onClick={handleModal}
        >
          <FontAwesomeIcon
            icon={regular("memo-circle-info")}
            className="text-blue-gray-700"
          />
        </button>
      </div>
    </div>
  );
};

export default NotCompletedTask;
